/* ------------------------------------------------------------------
   RAVA - APPLY ENGLISH THEME STRINGS

   The site's English mode is a class on <body>. Shopify's own store
   language is Hungarian, and it has no idea that class exists, so every
   label the theme renders through the translation filter comes out
   Hungarian even in English mode: Search, Cart, Skip to content, the
   cart drawer, pagination, form errors and so on.

   The English wording for all of those already ships with the theme in
   locales/en.default.json. rava-en-strings.js is generated from it, and
   this file puts it on the page.

   Exact whole-string matches only. Substring replacement would corrupt
   product names and customer-written text, and a label that is genuinely
   the same in both languages is skipped when the map is built.

   The right long-term fix is to publish English as a real Shopify
   language, at which point Shopify renders these itself and this becomes
   dead weight that can be removed. Until then it closes the gap.
   ------------------------------------------------------------------ */
(function () {
  'use strict';

  var MAP = window.RAVA_EN_STRINGS;
  if (!MAP) return;

  // Labels built around a value, such as "<product> mennyiségének növelése".
  // Compiled once, longest pattern first so a specific rule wins over a
  // looser one that would also match.
  var PATTERNS = (window.RAVA_EN_PATTERNS || [])
    .map(function (p) {
      try { return { re: new RegExp(p.re), to: p.to, len: p.re.length }; }
      catch (e) { return null; }
    })
    .filter(Boolean)
    .sort(function (a, b) { return b.len - a.len; });

  /* Hand-written corrections for text Shopify auto-translated badly. These
     live in the translation layer, which the theme cannot reach and which the
     app has no permission to edit, so they are repaired on the page.

     Fix them properly in Translate & Adapt when you get the chance, and these
     entries become harmless no-ops. */
  var CORRECTIONS = {
    // The machine turned the dash in "RAVA FEELING – BY JENŐ RÁCZ" into "for".
    'RAVA FEELING for BY JENŐ RÁCZ': 'RAVA FEELING — BY JENŐ RÁCZ',

    /* The chef knife's English name carries the size after a dash, and one of
       the generated patterns rewrites any "X – Y" into "X for Y". Putting the
       dash back made the two rules overwrite each other on every pass, so the
       size visibly flickered on the card and on the product page. The size is
       already in Technical details, so the name simply drops it. Every form
       the text can arrive in maps to the same result, which is what stops the
       flicker. */
    'Premium Damascus Chef Knife – 8"': 'Premium Damascus Chef Knife',
    'Premium Damascus Chef Knife for 8"': 'Premium Damascus Chef Knife',
    'Premium Damascus Chef Knife - 8"': 'Premium Damascus Chef Knife',
    'Premium Damascus Chef Knife – 8”': 'Premium Damascus Chef Knife',
    'Premium Damascus Chef Knife for 8”': 'Premium Damascus Chef Knife',
    'Premium Damascus Chef Knife - 8”': 'Premium Damascus Chef Knife'
  };

  function translate(value) {
    var s = value.trim();
    if (s.length < 4) return null;

    var out = null;
    if (CORRECTIONS[s]) out = CORRECTIONS[s];
    else if (MAP[s]) out = MAP[s];
    else {
      for (var i = 0; i < PATTERNS.length; i++) {
        if (s.match(PATTERNS[i].re)) { out = s.replace(PATTERNS[i].re, PATTERNS[i].to); break; }
      }
    }
    if (out === null) return null;

    /* A correction is the last word. Some generated patterns are broad enough
       to match their own output - "X – Y" becoming "X for Y" is one - so
       without this a pattern and a correction rewrite each other on every
       pass and the text flickers on screen. */
    if (CORRECTIONS[out]) out = CORRECTIONS[out];

    // Nothing to write, and writing it anyway would wake the observer again.
    if (out === s) return null;
    return out;
  }

  var ATTRS = ['placeholder', 'aria-label', 'alt', 'title'];
  var busy = false;

  /* Set when a mutation arrives while a pass is already running. Without it
     those records are dropped and never revisited, so a section that the
     theme injects at exactly the wrong moment - the related products, most
     visibly - stays in Hungarian until some later mutation happens to
     trigger another pass. */
  var pending = false;

  function english() {
    return document.body && document.body.classList.contains('en-lang');
  }

  function translateNode(node) {
    var raw = node.nodeValue;
    if (!raw) return false;
    var trimmed = raw.trim();
    if (trimmed.length < 4) return false;

    var hit = translate(trimmed);
    if (!hit) return false;

    // Keep whatever whitespace surrounded the original.
    var lead = raw.slice(0, raw.indexOf(trimmed));
    var tail = raw.slice(raw.indexOf(trimmed) + trimmed.length);
    node.nodeValue = lead + hit + tail;
    return true;
  }

  function walk(root) {
    var skip = { SCRIPT: 1, STYLE: 1, NOSCRIPT: 1, TEXTAREA: 1 };
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: function (n) {
        if (!n.parentElement) return NodeFilter.FILTER_REJECT;
        if (skip[n.parentElement.tagName]) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      }
    });
    var node;
    while ((node = walker.nextNode())) translateNode(node);
  }

  function walkAttrs(root) {
    var nodes = root.querySelectorAll ? root.querySelectorAll('[placeholder], [aria-label], [alt], [title]') : [];
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      for (var a = 0; a < ATTRS.length; a++) {
        var v = el.getAttribute(ATTRS[a]);
        if (!v) continue;
        var hit = translate(v);
        if (!hit) continue;
        el.setAttribute(ATTRS[a], hit);
        // A full alt translation carries the size and the material. The two
        // helpers below only know the bare product name, so they are told to
        // leave this image alone rather than overwrite the better text.
        if (ATTRS[a] === 'alt') el.setAttribute('data-rava-alt-done', '1');
      }
    }
  }

  /* Product names are content, not theme labels, so they are not in the map.
     The card snippets carry the English name on the image as a data attribute
     and it is moved into alt here. */
  function applyAltNames(root) {
    var nodes = root.querySelectorAll ? root.querySelectorAll('[data-rava-alt-en]') : [];
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el.hasAttribute('data-rava-alt-done')) continue;
      var en = el.getAttribute('data-rava-alt-en');
      if (!en) continue;
      if (!el.hasAttribute('data-rava-alt-hu')) {
        el.setAttribute('data-rava-alt-hu', el.getAttribute('alt') || '');
      }
      el.setAttribute('alt', en);
    }
  }

  /* Cart labels embed the product name: "Remove <name>", "Quantity for <name>".
     The label itself is translated by the patterns above, but the name inside
     it comes from Shopify and stays Hungarian. Each cart row already prints
     both names, so the English one is read off the row and substituted into
     that row's own labels. Scoped to the row so one product's name can never
     be written over another's. */
  function applyCartNames() {
    var rows = document.querySelectorAll('.cart-item, tr[id^="CartItem"]');
    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];
      var nameEl = row.querySelector('.cart-item__name');
      if (!nameEl) continue;

      var huEl = nameEl.querySelector('.hu-content');
      var enEl = nameEl.querySelector('.en-content');
      if (!huEl || !enEl) continue;

      var hu = huEl.textContent.trim();
      var en = enEl.textContent.trim();
      if (!en) continue;

      /* On the English locale Shopify already returns the translated product
         title, so both spans read the same. That is fine: the name shown is
         the English one either way, and the thumbnail below still needs it.
         Only the label substitution needs the two to differ. */

      if (hu && hu !== en) {
        var labels = row.querySelectorAll('.visually-hidden, [aria-label]');
        for (var j = 0; j < labels.length; j++) {
          var el = labels[j];
          if (el.closest('.cart-item__name')) continue;

          if (el.textContent && el.textContent.indexOf(hu) > -1) {
            el.textContent = el.textContent.split(hu).join(en);
          }
          var al = el.getAttribute('aria-label');
          if (al && al.indexOf(hu) > -1) {
            el.setAttribute('aria-label', al.split(hu).join(en));
          }
        }
      }

      /* The thumbnail's alt comes from the image in Shopify, not from the
         product title, so it is often the Hungarian name without the size
         and a substring match never finds it. The picture is of this line
         item, so the English name is simply the right description. */
      var imgs = row.querySelectorAll('img[alt]');
      for (var k = 0; k < imgs.length; k++) {
        var alt = imgs[k].getAttribute('alt');
        if (alt && alt.trim() !== en && alt.length < 80) {
          imgs[k].setAttribute('alt', en);
        }
      }
    }
  }

  /* The product page's own gallery takes its alt text from the media, which
     falls back to the Hungarian product name. The English name is already on
     the page as the heading, and these images are pictures of that product,
     so the heading is the right description. */
  function applyGalleryAlt() {
    var heading = null;
    var candidates = document.querySelectorAll('.product__title h1, .product__title h2, h1.product__title');
    for (var i = 0; i < candidates.length; i++) {
      if (candidates[i].offsetParent !== null) { heading = candidates[i]; break; }
    }
    if (!heading) return;

    var name = (heading.textContent || '').replace(/\s+/g, ' ').trim();
    if (!name) return;

    var imgs = document.querySelectorAll('.product__media img, .product-media-container img');
    for (var j = 0; j < imgs.length; j++) {
      if (imgs[j].hasAttribute('data-rava-alt-done')) continue;
      var alt = imgs[j].getAttribute('alt');
      // Only replace a name, never a written description.
      if (alt && alt.trim() !== name && alt.length < 80) imgs[j].setAttribute('alt', name);
    }
  }

  function apply(root) {
    if (!english() || busy) return;
    busy = true;
    try {
      var target = root && root.nodeType === 1 ? root : document.body;
      walk(target);
      walkAttrs(target);
      applyAltNames(target);
      applyCartNames();
      applyGalleryAlt();
      if (target !== document.body) { /* attributes on the root itself */
        for (var a = 0; a < ATTRS.length; a++) {
          var v = target.getAttribute && target.getAttribute(ATTRS[a]);
          var t3 = v ? translate(v) : null;
          if (t3) target.setAttribute(ATTRS[a], t3);
        }
      }
    } finally {
      /* Released on a timer, not on an animation frame, so our own edits do
         not re-enter through the observer below. A frame callback is the
         natural choice but it is throttled hard when the page is not being
         painted, which left a section injected at that moment sitting in
         Hungarian for as long as the throttling lasted. */
      setTimeout(function () {
        busy = false;
        if (!pending) return;
        pending = false;
        // Whatever arrived mid-pass was not looked at, so sweep the document.
        apply();
      }, 0);
    }
  }

  function boot() {
    apply();

    /* The theme and its apps inject sections after load - related products
       most visibly - and the observer below is what normally catches them.
       It cannot catch a section that arrives while a pass is already running
       and before the language class is on the body, so the first seconds are
       swept again on a fixed schedule. Each pass is cheap and idempotent:
       a string that has already been translated is no longer in the map. */
    var RETRIES = [300, 1000, 2500, 5000];
    for (var r = 0; r < RETRIES.length; r++) {
      window.setTimeout(function () { apply(); }, RETRIES[r]);
    }
    window.addEventListener('load', function () { apply(); });

    // The theme swaps the cart drawer, search results and product grids in
    // long after load, and apps inject later still.
    new MutationObserver(function (records) {
      if (!english()) return;
      if (busy) { pending = true; return; }
      for (var i = 0; i < records.length; i++) {
        var r = records[i];
        if (r.type === 'childList') {
          for (var j = 0; j < r.addedNodes.length; j++) {
            var n = r.addedNodes[j];
            if (n.nodeType === 1) apply(n);
            else if (n.nodeType === 3) translateNode(n);
          }
        } else if (r.type === 'characterData') {
          translateNode(r.target);
        }
      }
    }).observe(document.documentElement, {
      childList: true, subtree: true, characterData: true
    });

    // Switching language only flips a class, which the observer above ignores.
    new MutationObserver(function () { apply(); }).observe(document.body, {
      attributes: true, attributeFilter: ['class']
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
